import { getApiOrigin, getTrustedDownloadHosts } from "@/config/runtime";

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);

const normalizeHostname = (hostname: string) =>
  hostname.toLowerCase().replace(/^\[|\]$/g, "").replace(/\.$/, "");

const getApiHostname = (): string => {
  try {
    return normalizeHostname(new URL(getApiOrigin()).hostname);
  } catch {
    return "";
  }
};

const getWindowOrigin = () =>
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "http://localhost";

export function parseUrl(rawUrl: string, base: string = getWindowOrigin()) {
  return new URL(rawUrl, base);
}

/**
 * True for localhost, the configured API host, and any host equal to (or a
 * sub-domain of) an entry in `VITE_TRUSTED_DOWNLOAD_HOSTS`. Matching is on
 * whole DNS labels, so `evil-romdao.com` or `romdao.com.attacker.test` are
 * never trusted.
 */
export function isFirstPartyHostname(hostname: string): boolean {
  const normalized = normalizeHostname(hostname);
  if (!normalized) return false;
  if (LOCAL_HOSTNAMES.has(normalized)) return true;
  if (normalized === getApiHostname()) return true;
  return getTrustedDownloadHosts().some(
    (domain) => normalized === domain || normalized.endsWith(`.${domain}`)
  );
}

export function isSafeHttpUrl(rawUrl: string, options?: { allowHttpDev?: boolean }) {
  try {
    const parsed = parseUrl(rawUrl);
    if (parsed.protocol === "https:") return true;
    if (
      parsed.protocol === "http:" &&
      (options?.allowHttpDev || import.meta.env.DEV) &&
      isFirstPartyHostname(parsed.hostname)
    ) {
      return true;
    }
    return parsed.origin === getWindowOrigin() && parsed.pathname.startsWith("/");
  } catch {
    return rawUrl.startsWith("/");
  }
}

/**
 * True when `rawUrl` points at our own backend (page origin, configured API
 * origin, or a trusted download host), i.e. it is safe to send the session
 * token to it. Anything else (e.g. presigned R2/S3 URLs) must be opened
 * directly without credentials.
 */
export function isTrustedApiUrl(rawUrl: string): boolean {
  try {
    const parsed = parseUrl(rawUrl);
    return parsed.origin === getWindowOrigin() || isFirstPartyHostname(parsed.hostname);
  } catch {
    return rawUrl.startsWith("/");
  }
}

export function openNoOpener(rawUrl: string): boolean {
  if (!rawUrl || !isSafeHttpUrl(rawUrl)) return false;
  window.open(rawUrl, "_blank", "noopener,noreferrer");
  return true;
}

export function getSafeInternalPath(rawUrl: string | null | undefined, fallback = "/") {
  if (!rawUrl) return fallback;
  try {
    const parsed = parseUrl(rawUrl);
    if (parsed.origin !== getWindowOrigin() && !isFirstPartyHostname(parsed.hostname)) {
      return fallback;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return rawUrl.startsWith("/") && !rawUrl.startsWith("//") ? rawUrl : fallback;
  }
}

export function redactSensitiveSearch(search: string): string {
  if (!search) return "";
  const params = new URLSearchParams(search);
  for (const key of [...params.keys()]) {
    if (/token|secret|key|code|invite|share/i.test(key)) {
      params.set(key, "[REDACTED]");
    }
  }
  const redacted = params.toString();
  return redacted ? `?${redacted}` : "";
}

export function sanitizeCssColor(value: unknown, fallback = "#888"): string {
  if (typeof value !== "string") return fallback;
  const candidate = value.trim();
  if (/^#[0-9a-f]{3,8}$/i.test(candidate)) return candidate;
  if (/^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/i.test(candidate)) {
    return candidate;
  }
  return fallback;
}
